import {
  Banknote,
  BusFront,
  GraduationCap,
  HeartPulse,
  Home,
  Landmark,
  Leaf,
  ShieldCheck,
} from 'lucide-react';

import type { Topic } from '@/models/topic';

export const topics: Topic[] = [
  {
    id: 'education',
    label: 'Educação',
    description: 'Escolas, creches, jornada ampliada e reforço escolar.',
    keywords: ['educa', 'escola', 'creche', 'ensino', 'aula', 'professor', 'aluno', 'reforço'],
    questionFocus: 'o número de escolas municipais em tempo integral',
    positionFocus: 'a ampliação das escolas em tempo integral',
    missingFocus: 'a ampliação da jornada escolar',
    icon: GraduationCap,
    positionStatements: {
      disagree: 'Você discorda da ampliação das escolas em tempo integral.',
      partiallyDisagree: 'Você discorda em parte da ampliação das escolas em tempo integral.',
      neutral: 'Você não definiu uma posição sobre a ampliação das escolas em tempo integral.',
      partiallyAgree: 'Você concorda em parte com a ampliação das escolas em tempo integral.',
      agree: 'Você concorda com a ampliação das escolas em tempo integral.',
    },
  },
  {
    id: 'health',
    label: 'Saúde',
    description: 'Atenção básica, exames, consultas e unidades de saúde.',
    keywords: ['saude', 'posto', 'hospital', 'consulta', 'exame', 'vacina', 'atendimento', 'medic'],
    questionFocus: 'o atendimento básico nas unidades de saúde',
    positionFocus: 'a ampliação do atendimento básico nas unidades de saúde',
    missingFocus: 'a ampliação do atendimento básico',
    icon: HeartPulse,
    positionStatements: {
      disagree: 'Você discorda da ampliação do atendimento básico nas unidades de saúde.',
      partiallyDisagree:
        'Você discorda em parte da ampliação do atendimento básico nas unidades de saúde.',
      neutral:
        'Você não definiu uma posição sobre a ampliação do atendimento básico nas unidades de saúde.',
      partiallyAgree:
        'Você concorda em parte com a ampliação do atendimento básico nas unidades de saúde.',
      agree: 'Você concorda com a ampliação do atendimento básico nas unidades de saúde.',
    },
  },
  {
    id: 'security',
    label: 'Segurança',
    description: 'Prevenção, iluminação pública e patrulhamento comunitário.',
    keywords: ['seguranca', 'violenc', 'guarda', 'patrulha', 'ilumin', 'crime', 'prevencao'],
    questionFocus: 'a prevenção à violência e a iluminação pública',
    positionFocus: 'o reforço da prevenção à violência e da iluminação pública',
    missingFocus: 'o reforço da segurança nos bairros',
    icon: ShieldCheck,
    positionStatements: {
      disagree: 'Você discorda do reforço da prevenção à violência e da iluminação pública.',
      partiallyDisagree:
        'Você discorda em parte do reforço da prevenção à violência e da iluminação pública.',
      neutral:
        'Você não definiu uma posição sobre o reforço da prevenção à violência e da iluminação pública.',
      partiallyAgree:
        'Você concorda em parte com o reforço da prevenção à violência e da iluminação pública.',
      agree: 'Você concorda com o reforço da prevenção à violência e da iluminação pública.',
    },
  },
  {
    id: 'economy',
    label: 'Economia',
    description: 'Emprego, renda, negócios locais e simplificação de serviços.',
    keywords: ['econom', 'emprego', 'renda', 'negocio', 'negocios', 'empresa', 'trabalho', 'comerc'],
    questionFocus: 'o emprego local e o apoio a pequenos negócios',
    positionFocus: 'o apoio a pequenos negócios e a geração de renda',
    missingFocus: 'o apoio a pequenos negócios',
    icon: Banknote,
    positionStatements: {
      disagree: 'Você discorda do apoio a pequenos negócios e da geração de renda.',
      partiallyDisagree: 'Você discorda em parte do apoio a pequenos negócios e da geração de renda.',
      neutral: 'Você não definiu uma posição sobre o apoio a pequenos negócios e a geração de renda.',
      partiallyAgree: 'Você concorda em parte com o apoio a pequenos negócios e a geração de renda.',
      agree: 'Você concorda com o apoio a pequenos negócios e a geração de renda.',
    },
  },
  {
    id: 'mobility',
    label: 'Mobilidade',
    description: 'Transporte público, calçadas, ciclovias e trânsito.',
    keywords: ['mobilidade', 'onibus', 'transporte', 'ciclov', 'calcad', 'transito', 'rua'],
    questionFocus: 'o transporte público, as calçadas e as ciclovias',
    positionFocus: 'a ampliação do transporte público e das ciclovias',
    missingFocus: 'a melhoria do transporte e da circulação',
    icon: BusFront,
    positionStatements: {
      disagree: 'Você discorda da ampliação do transporte público e das ciclovias.',
      partiallyDisagree: 'Você discorda em parte da ampliação do transporte público e das ciclovias.',
      neutral: 'Você não definiu uma posição sobre a ampliação do transporte público e das ciclovias.',
      partiallyAgree: 'Você concorda em parte com a ampliação do transporte público e das ciclovias.',
      agree: 'Você concorda com a ampliação do transporte público e das ciclovias.',
    },
  },
  {
    id: 'environment',
    label: 'Meio ambiente',
    description: 'Resíduos, arborização urbana e cuidado climático.',
    keywords: ['meio ambiente', 'residuo', 'lixo', 'arvore', 'clima', 'sustent', 'coleta'],
    questionFocus: 'a coleta de resíduos, a arborização e o cuidado climático',
    positionFocus: 'a ampliação da coleta seletiva e da arborização urbana',
    missingFocus: 'ações de sustentabilidade urbana',
    icon: Leaf,
    positionStatements: {
      disagree: 'Você discorda da ampliação da coleta seletiva e da arborização urbana.',
      partiallyDisagree:
        'Você discorda em parte da ampliação da coleta seletiva e da arborização urbana.',
      neutral:
        'Você não definiu uma posição sobre a ampliação da coleta seletiva e da arborização urbana.',
      partiallyAgree:
        'Você concorda em parte com a ampliação da coleta seletiva e da arborização urbana.',
      agree: 'Você concorda com a ampliação da coleta seletiva e da arborização urbana.',
    },
  },
  {
    id: 'housing',
    label: 'Habitação',
    description: 'Moradia digna, regularização fundiária e urbanização.',
    keywords: ['habitacao', 'moradia', 'casa', 'aluguel', 'regulariza', 'risco', 'bairro'],
    questionFocus: 'moradia digna e a regularização fundiária',
    positionFocus: 'a ampliação da moradia digna e da regularização fundiária',
    missingFocus: 'a ampliação da moradia e regularização',
    icon: Home,
    positionStatements: {
      disagree: 'Você discorda da ampliação da moradia digna e da regularização fundiária.',
      partiallyDisagree:
        'Você discorda em parte da ampliação da moradia digna e da regularização fundiária.',
      neutral:
        'Você não definiu uma posição sobre a ampliação da moradia digna e da regularização fundiária.',
      partiallyAgree:
        'Você concorda em parte com a ampliação da moradia digna e da regularização fundiária.',
      agree: 'Você concorda com a ampliação da moradia digna e da regularização fundiária.',
    },
  },
  {
    id: 'public-management',
    label: 'Gestão pública',
    description: 'Transparência, serviços digitais e participação cidadã.',
    keywords: ['gestao', 'prefeitura', 'transparencia', 'digital', 'servico', 'atendimento', 'portal', 'dados'],
    questionFocus: 'a transparência da prefeitura e o atendimento digital',
    positionFocus: 'a transparência da prefeitura e o atendimento digital',
    missingFocus: 'a transparência e os serviços digitais',
    icon: Landmark,
    positionStatements: {
      disagree: 'Você discorda da transparência da prefeitura e do atendimento digital.',
      partiallyDisagree: 'Você discorda em parte da transparência da prefeitura e do atendimento digital.',
      neutral: 'Você não definiu uma posição sobre a transparência da prefeitura e o atendimento digital.',
      partiallyAgree: 'Você concorda em parte com a transparência da prefeitura e o atendimento digital.',
      agree: 'Você concorda com a transparência da prefeitura e o atendimento digital.',
    },
  },
];
